import {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useHistory} from 'react-router-dom';
import {Typography} from '~library/atoms/Typography';

import {DeleteBinLineIcon} from '~library/svg/DeleteBinLineIcon';
import {EditLineIcon} from '~library/svg/EditLineIcon';

import {isPreview} from '~/ViewInvitation/utils';
import {DONATION_MADE} from '~/ViewInvitation/reducers/constants';
import {refreshFundraiser} from '~/ViewInvitation/reducers/apiActions';
import {link} from '~libSass/base/_exports.sass';
import {Button} from '~library/atoms/Button';

const DonationSegment = ({donation, connectCTA, readonly}) => {
  const history = useHistory();
  const {eventId} = useSelector((state) => state.eventDetails);
  const {found, donation_url: donationUrl, id} = donation;
  const name = useSelector((state) => state.rsvp?.name ?? state.user?.name ?? '');
  const firstName = name?.split(' ')[0];
  const lastName = name?.split(' ')[1];
  const guestEmail = useSelector((state) => state.rsvp?.guestEmail ?? '');

  const hasDonation = !!donationUrl || donationUrl !== undefined; // along with initial state, prevents flash

  // get the key and host from the url that pledge sends back to us on create
  const tempUrl = document.createElement('a');
  tempUrl.href = donationUrl;
  const pledgelingHost = `${tempUrl.protocol}//${tempUrl.hostname}/assets/widget.js`;
  const partnerKey = tempUrl.pathname.split('/')[3];

  const dispatch = useDispatch();
  const preview = isPreview();

  const onMessage = (e) => {
    if (!e.origin.includes('pledge.to')) return;
    const {data} = e;
    let objMessage;
    try {
      objMessage = JSON.parse(data);
    } catch (err) {
      return; // we don't care about this
    }
    let value = parseFloat(objMessage.data?.amount);
    switch (objMessage.message) {
      case 'DonateCompleted':
        value *= 100; // store money in cents
        dispatch({
          type: DONATION_MADE,
          results: {
            donationUrl,
            donationAddedCents: value,
          },
        });
        dispatch(refreshFundraiser()); // tell backend to refresh cache
        break;
      case 'setWidgetHeight':
        // document.getElementById('external-donation-id').style.height =
        //   objMessage.data + 'px';
        break;
      case 'intersectionLoaded':
        break;
      default:
        evite.log('Pledge unhandled message: ', objMessage.message);
    }
  };

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (donationUrl) {
      const script = document.createElement('script');
      script.src = pledgelingHost;
      script.id = 'plg-widget';
      script.async = true;
      document.body.appendChild(script);

      window.addEventListener('message', onMessage, false);
      return () => {
        window.removeEventListener('message', onMessage);
      }; // will happen on unmount
    }
  }, [donationUrl]);

  if (found && donationUrl) {
    return (
      <>
        {preview && !readonly && (
          <div className="view-invitation__preview-cta multi">
            <Button
              variant="unstyled-button"
              className="view-invitation__preview-cta"
              onClick={() => history.push(`/${eventId}/gifting`)}
              data-qa-id="edit-donations"
              ref={(elem) => {
                if (connectCTA) connectCTA('edit-donations', elem);
              }}
            >
              <EditLineIcon ratio={0.5} color={link} />
              Edit
            </Button>
            <span>|</span>
            <Button
              variant="unstyled-button"
              className="view-invitation__preview-cta"
              data-qa-id="remove-donations"
              id="remove-donations"
              ref={(elem) => connectCTA && connectCTA('remove-donations', elem)}
            >
              <DeleteBinLineIcon ratio={0.5} color={link} />
              Remove
            </Button>
          </div>
        )}
        <div id="external-donation-id">
          {hasDonation ? (
            <div
              className="plg-donate"
              data-partner-key={partnerKey}
              data-item-id={id || window.event_id || window.evite_event.id}
              data-x-user-id={window.user_id || ''}
              data-first-name={firstName || ''}
              data-last-name={lastName || ''}
              data-email={guestEmail || ''}
              data-style-hide-social="true"
              data-style-transparent="true"
              data-relay-donated-event="true"
              data-template="branding-2022"
              data-qa-id="pledgeling-donate"
            />
          ) : // <Typography variant="paragraph3">
          //   {/* TODO: This text will need to be updated when multiple donation streams become supported */}
          //   Your host has removed donations for this event.
          // </Typography>
          null}
        </div>
      </>
    );
  }
  return (
    <Typography variant="paragraph3">Error loading donation, please try again later.</Typography>
  );
};

export const DonationsModule = (props) => {
  const {loading: isLoading, donations: propDonation, readonly, isCreationFlow} = props;
  const donation = useSelector((state) => state.donation);
  const donations = propDonation || donation.donations;
  const loading = isLoading || donation.loading;
  const [sortedDonations, setSortedDonations] = useState(donations);

  useEffect(() => {
    if (donations?.length > 0) {
      // since Charity goes first, lets reverse sort by fundraiser type because those values
      // can be Organization and Crowdfund, and Organization should be first
      setSortedDonations(
        donations.sort((a, b) => b.fundraiser_type?.localeCompare(a.fundraiser_type))
      );
    }
  }, [donations]);

  return (
    <div
      className={`donations-module__donation-segments ${
        isCreationFlow && !readonly ? 'with-edit-ctas' : ''
      }`}
    >
      <Typography variant="header3" classNames="view-invitation__registry-module-title">
        Donate to their chosen cause
      </Typography>
      {loading ? (
        <div className="view-invitation__donation-loading">
          <span className="evt-spinner inline dark" />
          <Typography variant="paragraph2">Loading...</Typography>
        </div>
      ) : (
        sortedDonations.map((d) =>
          // eslint-disable-next-line no-nested-ternary
          !d.donation_url ? (
            <Typography variant="paragraph3" key={d.id}>
              Error loading {/* eslint-disable-next-line no-nested-ternary */}
              {d.id?.includes('Organization')
                ? 'charity'
                : d.id?.includes('Crowdfund')
                  ? 'crowdfund'
                  : 'donation'}
              , please try again later.
            </Typography>
          ) : !d.found ? (
            <Typography variant="paragraph3">
              Your host has removed donations for this event.
            </Typography>
          ) : (
            <DonationSegment {...props} donation={d} key={d.id} />
          )
        )
      )}
    </div>
  );
};
